import { useEffect, useRef } from 'react';
import type { Broadcast, Design, KhutbahLine, Masjid, PowerState, TimetableDay } from '@noorview/core';
import { isBroadcastLive } from '@noorview/core';
import { Stage, type StageOptions } from '@noorview/display';
import { subscribeKhutbah } from '../data/khutbahFeed';

export interface StageViewProps {
  design: Design;
  masjid: Masjid;
  timetable: readonly TimetableDay[];
  mode?: StageOptions['mode'];
  power?: PowerState;
  fit?: StageOptions['fit'];
  quality?: StageOptions['quality'];
  now?: () => Date;
  onScale?: StageOptions['onScale'];
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  /** Blank during salah like the real screen (default yes). */
  salahBlank?: boolean;
  /** The screen's name, for the small footer along the bottom edge. */
  screenName?: string;
  /** Draw once and never tick (for small previews). */
  frozen?: boolean;
  /** Live messages and appeals to show over the design. */
  broadcasts?: readonly Broadcast[];
  /** Called with the live Stage once it exists (and with null when it goes away). */
  onStage?: (s: Stage | null) => void;
  /**
   * Build it a moment later, when the browser is free (default for everything except the Studio canvas), so opening a
   * page with many previews never freezes it. Previews are built one at a time and fade in as each is ready.
   */
  defer?: boolean;
}

/* One preview at a time, in the gaps between frames, so the page stays responsive while they're built. */
const jobs: Array<() => void> = [];
let pumping = false;
const idle = (f: () => void) => { const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback; if (ric) ric(f, { timeout: 250 }); else setTimeout(f, 16); };
/*
 * Moving between pages animates (the sidebar's highlight slides, the page fades). Building previews during that
 * would take the frames the animation needs, so the queue waits until the move has finished. The browser's
 * startViewTransition is wrapped once to know when one is running.
 */
let moving: Promise<unknown> | null = null;
if (typeof document !== 'undefined' && typeof document.startViewTransition === 'function') {
  const start = document.startViewTransition.bind(document);
  (document as Document & { startViewTransition: typeof start }).startViewTransition = ((arg: Parameters<typeof start>[0]) => {
    const t = start(arg);
    // Never wait on a move for more than a second: if the browser never reports it finished, previews still draw.
    const done = Promise.race([t.finished.catch(() => undefined), new Promise((r) => setTimeout(r, 1000))]);
    moving = done;
    void done.then(() => { if (moving === done) moving = null; });
    return t;
  }) as typeof start;
}
/*
 * The queue must never stop. A preview that fails to draw (or whose box has already left the page, for example the
 * old tab's thumbnails when switching tabs in Templates) is skipped and logged; the rest carry on. Before this, one
 * failure left the queue stuck and every preview stayed blank until the page was reloaded.
 */
function pump() {
  if (pumping) return;
  pumping = true;
  const next = () => {
    if (moving) { void moving.then(() => requestAnimationFrame(() => setTimeout(next, 0))); return; }
    const job = jobs.shift();
    if (!job) { pumping = false; return; }
    try { job(); } catch (e) { console.error('NoorView: a preview couldn’t be drawn', e); }
    idle(next);
  };
  // Let the page paint first.
  requestAnimationFrame(() => setTimeout(next, 0));
}
function later(job: () => void) { jobs.push(job); pump(); return () => { const i = jobs.indexOf(job); if (i >= 0) jobs.splice(i, 1); }; }

/** React wrapper around the display renderer: the exact same rendering the screens use. */
export function StageView(p: StageViewProps) {
  const host = useRef<HTMLDivElement>(null);
  const stage = useRef<Stage | null>(null);
  const onScale = useRef(p.onScale);
  onScale.current = p.onScale;
  const onStage = useRef(p.onStage);
  onStage.current = p.onStage;
  const latest = useRef(p);
  latest.current = p;
  // A live khutbah on this screen: what's said, as it comes (shared with every other preview of it).
  const said = useRef<KhutbahLine[]>([]);
  const khutbahId = p.broadcasts?.find((b) => b.kind === 'khutbah' && isBroadcastLive(b, new Date()))?.id;
  useEffect(() => {
    said.current = [];
    if (!khutbahId || p.frozen) return;
    return subscribeKhutbah(khutbahId, (lines) => { said.current = [...said.current, ...lines]; stage.current?.setKhutbahLines(lines); });
  }, [khutbahId, p.frozen]);

  useEffect(() => {
    const defer = p.defer ?? (p.mode ?? 'thumbnail') !== 'editor';
    let s: Stage | null = null;
    let gone = false;
    const build = () => {
    // This preview may have left the page while it waited its turn (React empties the box before it cancels the job).
    const el = host.current;
    if (gone || !el || !el.isConnected) return;
    const q = latest.current;
    s = new Stage(el, {
      mode: q.mode ?? 'thumbnail', masjid: q.masjid, timetable: q.timetable, fit: q.fit, quality: q.quality, now: q.now, frozen: q.frozen, salahBlank: q.salahBlank, screenName: q.screenName,
      onScale: (x) => onScale.current?.(x),
    });
    stage.current = s;
    s.setDesign(q.design);
    s.setPower(q.power ?? 'on');
    if (q.broadcasts) s.setBroadcasts(q.broadcasts);
    if (said.current.length) s.setKhutbahLines(said.current);
    s.layout();
    // Web fonts arrive after first paint; re-measure text once they're ready.
    if (document.fonts && document.fonts.status !== 'loaded') void document.fonts.ready.then(() => s?.refit());
    onStage.current?.(s);
    if (defer) host.current?.classList.add('stage-in');
    };
    const cancel = defer ? later(build) : (build(), () => {});
    return () => { gone = true; cancel(); if (s) { onStage.current?.(null); s.destroy(); } stage.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { stage.current?.setDesign(p.design); }, [p.design]);
  useEffect(() => { stage.current?.setData(p.masjid, p.timetable); }, [p.masjid, p.timetable]);
  useEffect(() => { stage.current?.setPower(p.power ?? 'on'); }, [p.power]);
  useEffect(() => { stage.current?.setBroadcasts(p.broadcasts ?? []); }, [p.broadcasts]);
  useEffect(() => { stage.current?.setOptions({ now: p.now, quality: p.quality, mode: p.mode, salahBlank: p.salahBlank, screenName: p.screenName }); }, [p.now, p.quality, p.mode, p.salahBlank, p.screenName]);

  return <div ref={host} className={p.className} style={p.style} role="img" aria-label={p.label ?? 'Screen preview'} />;
}
